import React, { useState, useEffect } from 'react';
import { Contact, SELF_CONTACT_TAG_NAME } from '../../types/contact';
import { ContactHistory } from './ContactHistory';
import { MergeContactsModal } from './MergeContactsModal';
import { Button } from '../ui/Button';
import { TagPill } from '../ui/TagPill';
import { EyeIcon, EyeOffIcon } from '../ui/icons';
import { formatBirthday } from '../../utils/birthdayUtils';
import { activityApi } from '../../services/activityApi';
import styles from './ContactDetail.module.css';

interface ContactDetailProps {
  contact: Contact;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  onBack: () => void;
  onContactUpdated: (contact: Contact) => void;
}

type TabType = 'details' | 'history';

export const ContactDetail: React.FC<ContactDetailProps> = ({
  contact,
  onEdit,
  onDelete,
  onBack,
  onContactUpdated,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [isHiddenFromFeed, setIsHiddenFromFeed] = useState<boolean | null>(null);
  const [unhiding, setUnhiding] = useState(false);

  const fullName = `${contact.firstName} ${contact.lastName}`;
  const subtitle = [contact.title, contact.company].filter(Boolean).join(' at ');
  const isMeContact = contact.tags.some((contactTag) => contactTag.tag.name.toLowerCase() === SELF_CONTACT_TAG_NAME.toLowerCase());
  const visibleTags = contact.tags.filter((contactTag) => contactTag.tag.name.toLowerCase() !== SELF_CONTACT_TAG_NAME.toLowerCase());

  // Fetch hidden status when contact changes
  useEffect(() => {
    const fetchHiddenStatus = async () => {
      try {
        const hidden = await activityApi.isContactHidden(contact.id);
        setIsHiddenFromFeed(hidden);
      } catch (err) {
        console.error('Error fetching hidden status:', err);
        setIsHiddenFromFeed(false);
      }
    };
    fetchHiddenStatus();
  }, [contact.id]);

  const handleUnhide = async () => {
    setUnhiding(true);
    try {
      await activityApi.unhideContact(contact.id);
      setIsHiddenFromFeed(false);
    } catch (err) {
      console.error('Error unhiding contact:', err);
    } finally {
      setUnhiding(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete();
    } catch (err) {
      console.error('Error deleting contact:', err);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const primaryEmail = contact.emails.find(e => e.isPrimary) || contact.emails[0];
  const primaryPhone = contact.phones.find(p => p.isPrimary) || contact.phones[0];
  const otherEmails = contact.emails.filter(e => e !== primaryEmail);
  const otherPhones = contact.phones.filter(p => p !== primaryPhone);

  return (
    <div className={styles.container}>
      <div className={`${styles.header} mobile-page-header-anchor`}>
        <Button variant="secondary" size="md" onClick={onBack}>
          &larr; Back
        </Button>
        <div className={styles.actions}>
          <Button variant="secondary" size="md" onClick={() => setShowMergeModal(true)}>
            Merge
          </Button>
          <Button variant="primary" size="md" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className={styles.profile}>
        <div className={styles.nameRow}>
          <h1 className={styles.name}>{fullName}</h1>
          {isMeContact && (
            <TagPill
              name={SELF_CONTACT_TAG_NAME}
              size="md"
              className={styles.identityBadge}
            />
          )}
        </div>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>

      {isHiddenFromFeed && (
        <div className={styles.hiddenBanner}>
          <div className={styles.hiddenBannerContent}>
            <EyeOffIcon size={18} className={styles.hiddenIcon} />
            <span className={styles.hiddenText}>
              This contact is hidden from your activity feed
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUnhide}
            disabled={unhiding}
            loading={unhiding}
            leftIcon={<EyeIcon size={14} />}
          >
            {unhiding ? 'Unhiding...' : 'Show in Feed'}
          </Button>
        </div>
      )}

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'details' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'history' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'details' ? (
          <div className={styles.details}>
            {(contact.emails.length > 0 || contact.phones.length > 0) && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Contact Information</h2>
                
                {contact.emails.length > 0 && (
                  <div className={styles.infoGroup}>
                    <h3 className={styles.infoLabel}>Email</h3>
                    <div className={styles.infoList}>
                      {primaryEmail && (
                        <div className={`${styles.infoItem} ${!primaryEmail.isValid ? styles.invalidItem : ''}`}>
                          {primaryEmail.isValid ? (
                            <a href={`mailto:${primaryEmail.email}`} className={styles.link}>
                              {primaryEmail.email}
                            </a>
                          ) : (
                            <span className={styles.invalidValue}>{primaryEmail.email}</span>
                          )}
                          <span className={styles.itemLabel}>({primaryEmail.label})</span>
                          <span className={styles.primaryBadge}>Primary</span>
                          {!primaryEmail.isValid && (
                            <span className={styles.invalidBadge}>Invalid</span>
                          )}
                        </div>
                      )}
                      {otherEmails.map((email, index) => (
                        <div key={index} className={`${styles.infoItem} ${!email.isValid ? styles.invalidItem : ''}`}>
                          {email.isValid ? (
                            <a href={`mailto:${email.email}`} className={styles.link}>
                              {email.email}
                            </a>
                          ) : (
                            <span className={styles.invalidValue}>{email.email}</span>
                          )}
                          <span className={styles.itemLabel}>({email.label})</span>
                          {!email.isValid && (
                            <span className={styles.invalidBadge}>Invalid</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {contact.phones.length > 0 && (
                  <div className={styles.infoGroup}>
                    <h3 className={styles.infoLabel}>Phone</h3>
                    <div className={styles.infoList}>
                      {primaryPhone && (
                        <div className={`${styles.infoItem} ${!primaryPhone.isValid ? styles.invalidItem : ''}`}>
                          {primaryPhone.isValid ? (
                            <a href={`tel:${primaryPhone.phone}`} className={styles.link}>
                              {primaryPhone.phone}
                            </a>
                          ) : (
                            <span className={styles.invalidValue}>{primaryPhone.phone}</span>
                          )}
                          <span className={styles.itemLabel}>({primaryPhone.label})</span>
                          <span className={styles.primaryBadge}>Primary</span>
                          {!primaryPhone.isValid && (
                            <span className={styles.invalidBadge}>Invalid</span>
                          )}
                        </div>
                      )}
                      {otherPhones.map((phone, index) => (
                        <div key={index} className={`${styles.infoItem} ${!phone.isValid ? styles.invalidItem : ''}`}>
                          {phone.isValid ? (
                            <a href={`tel:${phone.phone}`} className={styles.link}>
                              {phone.phone}
                            </a>
                          ) : (
                            <span className={styles.invalidValue}>{phone.phone}</span>
                          )}
                          <span className={styles.itemLabel}>({phone.label})</span>
                          {!phone.isValid && (
                            <span className={styles.invalidBadge}>Invalid</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {contact.linkedInUrl && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Social Profiles</h2>
                <div className={styles.infoGroup}>
                  <h3 className={styles.infoLabel}>LinkedIn</h3>
                  <div className={styles.infoItem}>
                    <a
                      href={contact.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                      {contact.linkedInUrl}
                    </a>
                  </div>
                </div>
              </section>
            )}

            {contact.birthday && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Personal Information</h2>
                <div className={styles.infoGroup}>
                  <h3 className={styles.infoLabel}>Birthday</h3>
                  <div className={styles.infoItem}>
                    {formatBirthday(contact.birthday)}
                  </div>
                </div>
              </section>
            )}

            {contact.notes && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Notes</h2>
                <p className={styles.notes}>{contact.notes}</p>
              </section>
            )}

            {visibleTags.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Tags</h2>
                <div className={styles.tagsContainer}>
                  {visibleTags.map((contactTag) => (
                    <TagPill
                      key={contactTag.id}
                      name={contactTag.tag.name}
                      size="md"
                    />
                  ))}
                </div>
              </section>
            )}

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Metadata</h2>
              <div className={styles.metadata}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Created:</span>
                  <span className={styles.metaValue}>
                    {new Date(contact.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Last Updated:</span>
                  <span className={styles.metaValue}>
                    {new Date(contact.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            </section>
          </div>
        ) : (
          <ContactHistory 
            contactId={contact.id} 
            onRestore={onContactUpdated}
          />
        )}
      </div>

      {showDeleteConfirm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Delete Contact</h3>
            <p className={styles.modalText}>
              Are you sure you want to delete {fullName}? This action cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={handleDelete}
                disabled={deleting}
                loading={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showMergeModal && (
        <MergeContactsModal
          primaryContact={contact}
          onMergeComplete={(mergedContact) => {
            setShowMergeModal(false);
            onContactUpdated(mergedContact);
          }}
          onClose={() => setShowMergeModal(false)}
        />
      )}
    </div>
  );
};

export default ContactDetail;
